import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog.jsx';
import { Progress } from '../components/ui/progress.jsx';
import { Button } from '../components/ui/button.jsx';
import { 
  Loader2, 
  Check, 
  Download, 
  Music, 
  Drum, 
  Radio, 
  Piano, 
  Mic2, 
  BarChart3,
  CheckCircle,
  AlertCircle,
  Activity
} from 'lucide-react';
import api from '../services/api';

const ANALYSIS_PHASES = {
  starting: {
    title: "Initializing",
    icon: Activity,
    description: "Preparing audio analysis...",
    estimatedTime: "~2 seconds"
  },
  downloading: {
    title: "Loading Audio",
    icon: Download,
    description: "Downloading audio file from storage...",
    estimatedTime: "~5 seconds"
  },
  loading: {
    title: "Processing Audio",
    icon: Music,
    description: "Loading and preprocessing audio data...",
    estimatedTime: "~5 seconds"
  },
  bpm: {
    title: "Detecting BPM",
    icon: Activity,
    description: "Analyzing tempo and beat patterns...",
    estimatedTime: "~5 seconds"
  },
  key: {
    title: "Analyzing Key",
    icon: Piano,
    description: "Detecting musical key and mode using chroma features...",
    estimatedTime: "~5 seconds"
  },
  chords: {
    title: "Extracting Chords",
    icon: Music,
    description: "Identifying chord progressions throughout the track...",
    estimatedTime: "~10 seconds"
  },
  melody: {
    title: "Analyzing Melody",
    icon: Mic2,
    description: "Tracking pitch contour and melodic patterns...",
    estimatedTime: "~10 seconds"
  },
  drums: {
    title: "Analyzing Drums",
    icon: Drum,
    description: "Detecting rhythm patterns and percussion complexity...",
    estimatedTime: "~5 seconds"
  },
  spectral: {
    title: "Spectral Analysis",
    icon: Radio,
    description: "Computing frequency spectrum characteristics...",
    estimatedTime: "~5 seconds"
  },
  instruments: {
    title: "Identifying Instruments",
    icon: BarChart3,
    description: "Detecting harmonic and percussive elements...",
    estimatedTime: "~5 seconds"
  },
  sections: {
    title: "Detecting Sections",
    icon: Music,
    description: "Identifying song structure (intro, verse, chorus, etc.)...",
    estimatedTime: "~5 seconds"
  },
  genre: {
    title: "Detecting Genre",
    icon: Radio,
    description: "Classifying musical genre and style...",
    estimatedTime: "~3 seconds"
  },
  complete: {
    title: "Analysis Complete",
    icon: CheckCircle,
    description: "All audio features have been analyzed!",
    estimatedTime: ""
  },
  error: {
    title: "Error",
    icon: AlertCircle,
    description: "Something went wrong during analysis",
    estimatedTime: ""
  }
};

const PHASES_ORDER = [
  'starting', 
  'downloading', 
  'loading', 
  'bpm', 
  'key', 
  'chords', 
  'melody', 
  'drums', 
  'spectral', 
  'instruments',
  'sections',
  'genre',
  'complete'
];

function PhaseIndicator({ phase, currentPhase, isComplete }) {
  const config = ANALYSIS_PHASES[phase] || ANALYSIS_PHASES.starting;
  const Icon = config.icon;
  
  const phaseIndex = PHASES_ORDER.indexOf(phase);
  const currentIndex = PHASES_ORDER.indexOf(currentPhase);
  const isPast = phaseIndex < currentIndex || isComplete;
  const isCurrent = phase === currentPhase && !isComplete;
  
  return (
    <div className={`flex items-center gap-3 py-1.5 transition-all duration-300 ${
      isPast ? 'text-green-500' : 
      isCurrent ? 'text-primary font-medium' : 
      'text-muted-foreground/50'
    }`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
        isPast ? 'bg-green-500/20' : 
        isCurrent ? 'bg-primary/20' : 
        'bg-muted/30'
      }`}>
        {isPast ? (
          <Check className="w-3.5 h-3.5" />
        ) : isCurrent ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Icon className="w-3.5 h-3.5" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">{config.title}</span>
        {isCurrent && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{config.description}</p>
        )}
      </div>
      {!isPast && !isCurrent && config.estimatedTime && (
        <span className="text-xs text-muted-foreground flex-shrink-0">{config.estimatedTime}</span>
      )}
    </div>
  );
}

export default function AnalysisProgressModal({ isOpen, sessionId, onClose, onComplete }) {
  const [progress, setProgress] = useState({
    phase: 'starting',
    message: 'Initializing analysis...',
    percent: 0
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const pollIntervalRef = useRef(null);
  const hasSeenStartRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !sessionId) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/analysis-status`);
        const data = response.data;
        
        if (data.analysis_progress) {
          const phase = data.analysis_progress.phase;
          
          if (phase !== 'complete' && phase !== 'idle') {
            hasSeenStartRef.current = true;
          }
          
          setProgress(data.analysis_progress);
          
          if (phase === 'error') {
            setErrorMessage(data.analysis_progress.message || 'An error occurred during analysis');
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
          
          if (phase === 'complete' && hasSeenStartRef.current) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            if (data.track_analysis) {
              onComplete?.(data.track_analysis);
            }
          }
        }
      } catch (error) {
        console.error('Error polling analysis status:', error);
      }
    };

    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, 1000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOpen, sessionId, onComplete]);

  useEffect(() => {
    if (isOpen) {
      setProgress({
        phase: 'starting',
        message: 'Initializing analysis...',
        percent: 0
      });
      setErrorMessage(null);
      hasSeenStartRef.current = false;
    }
  }, [isOpen]);

  const currentPhase = progress.phase || 'starting';
  const isComplete = currentPhase === 'complete';
  const isError = currentPhase === 'error';

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="analysis-progress-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {!isComplete && !isError && (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            )}
            {isComplete && <CheckCircle className="h-5 w-5 text-green-500" />}
            {isError && <AlertCircle className="h-5 w-5 text-red-500" />}
            <span>
              {isComplete ? 'Analysis Complete!' : 
               isError ? 'Analysis Failed' : 
               'Analyzing Track'}
            </span>
          </DialogTitle>
          <DialogDescription>
            {isComplete ? 'Your track has been fully analyzed. View the results below.' :
             isError ? 'There was a problem analyzing your track.' :
             'Extracting audio features and musical characteristics...'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{progress.message}</span>
              <span className="text-primary font-medium">{progress.percent}%</span>
            </div>
            <Progress value={progress.percent} className="w-full h-2" />
          </div>
          
          <div className="border rounded-lg p-3 bg-muted/20 max-h-[300px] overflow-y-auto">
            <div className="space-y-0.5">
              {PHASES_ORDER.map((phase) => (
                <PhaseIndicator 
                  key={phase} 
                  phase={phase} 
                  currentPhase={currentPhase}
                  isComplete={isComplete}
                />
              ))}
            </div>
          </div>
          
          {isError && errorMessage && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-500">{errorMessage}</p>
            </div>
          )}
          
          {isComplete && (
            <div className="flex justify-center pt-2">
              <Button onClick={handleClose} className="w-full">
                <Check className="h-4 w-4 mr-2" />
                View Results
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
